import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Heading, VStack, FormControl, FormLabel,
    Input, Select, Button, useToast,
    Alert, AlertIcon, Card, CardBody, Text, Spinner, Center,
    HStack, Badge, Divider, List, ListItem, ListIcon, Flex, Checkbox, Spacer, Icon, IconButton
} from '@chakra-ui/react';
import { FiCalendar, FiCheckCircle, FiTrash2, FiCoffee } from 'react-icons/fi';
import { authAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from '../components/LoadingOverlay';
import LogoPulseLoader from '../components/LogoPulseLoader';

// 當天訂餐截止小時（台北時間）。家長原生 App 把此頁打包在內，原生 App 要等下次更新才生效。
const LUNCH_ORDER_CUTOFF_HOUR = 15;

const ParentLunchPage = () => {
    const toast = useToast();

    // State
    const [children, setChildren] = useState([]);
    const [selectedStudentId, setSelectedStudentId] = useState('');
    const [existingOrders, setExistingOrders] = useState([]);
    const [selectedDates, setSelectedDates] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' });

    // Fetch Children
    useEffect(() => {
        const init = async () => {
            try {
                const res = await authAPI.getMe();
                if (res.data.children?.length > 0) {
                    setChildren(res.data.children);
                    setSelectedStudentId(res.data.children[0].id);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setInitialLoading(false);
            }
        };
        init();
    }, []);

    // Fetch Existing Orders
    useEffect(() => {
        if (selectedStudentId) {
            fetchOrders(selectedStudentId);
            setSelectedDates(new Set());
        }
    }, [selectedStudentId]);

    const fetchOrders = async (studentId) => {
        try {
            const res = await authAPI.lunch.getOrders({
                student_id: studentId,
                start_date: todayStr
            });
            setExistingOrders(res.data);
        } catch (e) {
            console.error(e);
        }
    };

    const toggleDate = (date) => {
        const newSet = new Set(selectedDates);
        if (newSet.has(date)) newSet.delete(date);
        else newSet.add(date);
        setSelectedDates(newSet);
    };

    const handleCancel = async (orderId, date) => {
        if (!window.confirm(`確定要取消 ${date} 的訂餐嗎？`)) return;

        setLoading(true);
        try {
            await authAPI.lunch.cancelOrder(orderId);
            toast({ title: "取消成功", status: "success" });
            fetchOrders(selectedStudentId); // Refresh list
        } catch (error) {
            console.error("Cancel failed", error);
            toast({ title: "取消失敗", description: "無法取消此訂單，可能已超過取消期限。", status: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!selectedStudentId) return;
        setLoading(true);
        try {
            await authAPI.lunch.batchOrder({
                student_id: parseInt(selectedStudentId),
                dates: Array.from(selectedDates)
            });
            toast({ title: "訂餐成功", status: "success" });
            fetchOrders(selectedStudentId);
            setSelectedDates(new Set());
        } catch (error) {
            toast({ title: "訂餐失敗", description: error.response?.data?.detail, status: "error" });
        } finally {
            setLoading(false);
        }
    };

    // Generate next 14 weekdays (Starting Today, UTC+8)
    const datesList = useMemo(() => {
        const list = [];

        // Cut-off Logic (Asia/Taipei)
        const nowTaipei = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Taipei' }));
        // If current hour >= cutoff, start from tomorrow (offset 1), else start from today (offset 0)
        let dayOffset = nowTaipei.getHours() >= LUNCH_ORDER_CUTOFF_HOUR ? 1 : 0;

        while (list.length < 14) {
            const d = new Date();
            d.setDate(d.getDate() + dayOffset);

            // Get YYYY-MM-DD in Asia/Taipei
            const dateStr = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' });

            // Get Weekday in Asia/Taipei
            const weekday = d.toLocaleDateString('en-US', { timeZone: 'Asia/Taipei', weekday: 'short' });

            if (weekday !== 'Sat' && weekday !== 'Sun') {
                if (!list.includes(dateStr)) list.push(dateStr);
            }
            dayOffset++;
            if (dayOffset > 30) break;
        }
        return list;
    }, []);

    const isOrdered = (date) => existingOrders.some(o => o.date === date);

    return (
        <VStack spacing={0} align="stretch" pb="150px" bg="gray.50" minH="100vh">
            {initialLoading ? (
                <Flex justify="center" align="center" py={10}>
                    <LogoPulseLoader />
                </Flex>
            ) : children.length === 0 ? (
                <Alert status="warning" m={5} borderRadius="xl"><AlertIcon />目前沒有綁定的學生資料</Alert>
            ) : (
                <>
                    {/* Student Selector */}
                    <Box bg="white" p={5} pb={6} shadow="sm" borderBottomRadius="3xl" mb={4} zIndex={10}>
                        <FormControl>
                            <FormLabel fontSize="lg" fontWeight="bold" color="gray.600">訂餐學生</FormLabel>
                            <Select
                                size="lg" h="50px" borderRadius="xl" bg="gray.100" border="none"
                                value={selectedStudentId}
                                onChange={e => setSelectedStudentId(e.target.value)}
                            >
                                {children.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Menu List */}
                    <VStack px={5} spacing={4} align="stretch">
                        <HStack justify="space-between" px={1}>
                            <Text fontSize="lg" fontWeight="bold" color="gray.500">最多可預訂到未來兩週</Text>
                            <Text fontSize="sm" color="gray.400">系統自動排除週末</Text>
                        </HStack>

                        {datesList.map(date => {
                            const ordered = isOrdered(date);
                            const selected = selectedDates.has(date);
                            const dObj = new Date(date);
                            const weekStr = ['日', '一', '二', '三', '四', '五', '六'][dObj.getDay()];

                            return (
                                <Card
                                    key={date}
                                    bg={ordered ? "gray.50" : "white"}
                                    shadow={selected ? "md" : "sm"}
                                    borderRadius="2xl"
                                    border={selected ? "2px solid" : "none"}
                                    borderColor="brand.400"
                                    opacity={ordered ? 0.7 : 1}
                                    onClick={() => !ordered && toggleDate(date)}
                                    _active={{ transform: ordered ? 'none' : 'scale(0.98)' }}
                                    transition="all 0.1s"
                                    cursor={ordered ? "default" : "pointer"}
                                >
                                    <CardBody p={4} py={5}>
                                        <HStack justify="space-between">
                                            <HStack spacing={4}>
                                                <Center boxSize="54px" bg={ordered ? "gray.200" : "orange.50"} borderRadius="2xl">
                                                    <Icon as={FiCoffee} boxSize={6} color={ordered ? "gray.400" : "orange.500"} />
                                                </Center>
                                                <VStack align="start" spacing={0}>
                                                    <Text fontSize="xl" fontWeight="bold" color={ordered ? "gray.500" : "gray.800"}>
                                                        {dObj.toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' })}
                                                    </Text>
                                                    <Text fontSize="md" color={ordered ? "gray.400" : "gray.500"}>星期{weekStr}</Text>
                                                </VStack>
                                            </HStack>

                                            {ordered ? (
                                                <HStack spacing={3}>
                                                    <Badge colorScheme="green" fontSize="md" px={3} py={1} borderRadius="lg">已預訂</Badge>
                                                    <IconButton
                                                        icon={<FiTrash2 />}
                                                        aria-label="取消訂餐"
                                                        colorScheme="red"
                                                        variant="ghost"
                                                        size="sm"
                                                        isRound
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const order = existingOrders.find(o => o.date === date);
                                                            if (order) handleCancel(order.id, date);
                                                        }}
                                                    />
                                                </HStack>
                                            ) : (
                                                <Checkbox
                                                    isChecked={selected}
                                                    size="lg"
                                                    colorScheme="brand"
                                                    pointerEvents="none"
                                                    iconColor="white"
                                                    borderColor="gray.300"
                                                />
                                            )}
                                        </HStack>
                                    </CardBody>
                                </Card>
                            );
                        })}
                    </VStack>

                    {/* Sticky Footer for Confirmation */}
                    {selectedDates.size > 0 && (
                        <Box
                            position="fixed" bottom={0} left={0} right={0}
                            bg="white" p={5} shadow="2xl" borderTopRadius="3xl" zIndex={100}
                            pb="max(20px, env(safe-area-inset-bottom))"
                            boxShadow="0px -4px 20px rgba(0,0,0,0.1)"
                        >
                            <HStack justify="space-between" align="center">
                                <VStack align="start" spacing={0}>
                                    <Text fontSize="sm" color="gray.500">已選擇</Text>
                                    <Text fontSize="3xl" fontWeight="bold" color="brand.600">{selectedDates.size} <Text as="span" fontSize="lg" color="gray.400">餐</Text></Text>
                                </VStack>
                                <Button
                                    size="lg" h="64px" w="60%"
                                    colorScheme="brand" borderRadius="2xl" shadow="lg"
                                    fontSize="xl"
                                    onClick={handleSubmit}
                                    isLoading={loading}
                                >
                                    確認訂餐
                                </Button>
                            </HStack>
                        </Box>
                    )}
                </>
            )}
        </VStack>
    );
};

export default ParentLunchPage;
